import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { first } from 'rxjs/operators';
import { ImageService } from '../../../_services/image.service';
import { AlertService } from '../../../_services';
import { User } from '../../../_models';
import { Image } from '../../../_models/image';
import { UtilsService } from '../../../_services/utils.service';
import { UploadS3Service } from '../../../_services/upload-s3.service';
import { Constants } from '../../../constants/constants';

@Component({
  selector: 'app-upload-image-dialog',
  templateUrl: './upload-image-dialog.component.html',
  styleUrls: ['./upload-image-dialog.component.scss']
})
export class UploadImageDialogComponent implements OnInit {
  file: File = null;
  currentUser: User;
  submitted = false;
  selectionError = false;
  dataType: string;
  errorMsg = '';
  image: Image;
  Constants = Constants;

  // Drag-and-drop / preview state
  imagePreviewUrl: string = null;
  isDragging = false;
  filePath: string;

  constructor(
    private dialogRef: MatDialogRef<UploadImageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private imageService: ImageService,
    private uploadS3Service: UploadS3Service,
    private utilsService: UtilsService,
    private alertService: AlertService
  ) {
    this.currentUser = this.utilsService.getCurrentUser();
    this.image = new Image();
    this.image.merchantId = this.currentUser.merchantId;
    this.image.isCategory = data.isCategory;
    this.image.cloverDataName = data.name;
    this.image.cloverDataId = data.id;
    this.image.s3ContainerKey = data.s3ContainerKey;

    // Show existing image if available
    if (data.imageData) {
      this.imagePreviewUrl = data.imageData;
    }
  }

  ngOnInit(): void {
    this.dataType = this.image.isCategory ? 'Category' : 'Item';
  }

  // ── Drag and drop ──

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  private handleFile(file: File): void {
    if (!file.type.startsWith('image/')) {
      this.selectionError = true;
      this.errorMsg = 'Please select an image file';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.selectionError = true;
      this.errorMsg = 'Image must be under 5MB';
      return;
    }
    this.file = file;
    this.selectionError = false;
    this.errorMsg = '';
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  // ── File input ──

  async onSelect(): Promise<void> {
    const fileInput = document.getElementById('myFile') as HTMLInputElement;
    if (fileInput.files && fileInput.files.length > 0) {
      this.handleFile(fileInput.files[0]);
    }
  }

  // ── Base64 utility ──

  async getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = err => reject(err);
    });
  }

  // ── Save (Clover) ──

  async save(): Promise<void> {
    this.submitted = true;
    this.selectionError = false;
    this.errorMsg = '';

    if (this.file) {
      await this.uploadAndSave();
      return;
    }

    const url = (document.getElementById('image-url') as HTMLInputElement).value;
    if (!url || url.length < 3) {
      this.selectionError = true;
      this.errorMsg = 'Please select an image or enter a URL';
      this.submitted = false;
      return;
    }

    this.image.imageData = url;
    this.imageService.updateImage(this.image)
      .pipe(first())
      .subscribe(
        () => this.alertService.success('Image Uploaded', true),
        error => this.alertService.error(error)
      );

    this.dialogRef.close({
      returnImage: this.image.imageData,
      s3FileName: this.filePath
    });
  }

  private async uploadAndSave(): Promise<void> {
    // Store the uploaded image as an LZ-compressed data-URL in Mongo via
    // POST /images/update (the backend compresses `imageData` server-side),
    // mirroring the Square path. The storefront/signage read path decompresses
    // `imageData` directly, so Mongo is the source of truth for rendered item
    // and category images. We intentionally do NOT push to S3 from the browser.
    this.image.s3ContainerKey = '';
    try {
      this.image.imageData = await this.getBase64(this.file);

      this.imageService.updateImage(this.image)
        .pipe(first())
        .subscribe(
          () => this.alertService.success('Image Uploaded', true),
          error => this.alertService.error(error)
        );

      this.dialogRef.close({
        returnImage: this.image.imageData,
        s3FileName: ''
      });
    } catch (e) {
      this.alertService.error('Upload failed');
      this.submitted = false;
    }
  }

  // ── Save (Square) ──

  async saveSquare(): Promise<void> {
    this.submitted = true;
    this.selectionError = false;
    this.errorMsg = '';

    const url = (document.getElementById('image-url') as HTMLInputElement).value;

    if (!this.file && (!url || url.length < 3)) {
      this.selectionError = true;
      this.errorMsg = 'Please select an image or enter a URL';
      this.submitted = false;
      return;
    }

    const image: any = {
      cloverDataId: this.image.cloverDataId,
      cloverDataName: this.image.cloverDataId,
      isCategory: this.image.isCategory,
      imageData: '',
      s3ContainerKey: this.image.s3ContainerKey
    };

    if (this.file) {
      image.imageData = await this.getBase64(this.file);
    } else {
      image.imageData = url;
    }

    this.imageService.updateImage(image)
      .pipe(first())
      .subscribe(
        () => this.alertService.success('Image Uploaded', true),
        error => this.alertService.error(error)
      );

    this.dialogRef.close({ returnImage: image.imageData });
  }

  // ── Delete ──

  async delete(): Promise<void> {
    this.submitted = true;
    this.selectionError = false;

    this.image.imageData = '';
    if (this.image.s3ContainerKey) {
      // Best-effort S3 cleanup for legacy S3-backed images. Never let a failed
      // or missing S3 delete block the authoritative Mongo delete below.
      try {
        await this.uploadS3Service.deleteFile(this.image.s3ContainerKey);
      } catch (e) {
        // ignore — image record is removed from Mongo regardless
      }
    }
    this.imageService.deleteByCloverDataId(this.image.cloverDataId)
      .pipe(first())
      .subscribe(
        () => {
          this.alertService.success('Image Deleted', true);
          this.dialogRef.close({ image: '' });
        },
        error => {
          this.alertService.error(error);
          this.submitted = false;
        }
      );
  }

  async deleteSquare(): Promise<void> {
    this.submitted = true;
    this.imageService.delete(this.image.cloverDataId).subscribe(
      () => {
        this.alertService.success('Image Deleted', true);
        this.dialogRef.close({ returnImage: '' });
      },
      error => {
        this.alertService.error(error);
        this.submitted = false;
      }
    );
  }

  // ── Misc ──

  generateImage(): void {
    this.submitted = true;
    this.imageService.generateAndSaveImage(this.image.cloverDataName, this.image.isCategory, this.image.cloverDataId)
      .subscribe((response: any) => {
        this.alertService.success('Image Generated', true);
        this.dialogRef.close({ returnImage: response?.imageData });
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
